import { shortFormatDate } from '@liveauctioneers/caterwaul-components/lib/utils/dates';
import { Checkbox } from '@liveauctioneers/hammer-ui-core/checkbox';
import { Chip } from '@liveauctioneers/hammer-ui-core/chips';
import { IconButton } from '@liveauctioneers/hammer-ui-core/iconButton';
import { Trash } from '@liveauctioneers/hammer-ui-core/icons';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { Caption } from '@liveauctioneers/hammer-ui-core/text';
import { parseISO } from 'date-fns';

import type { Facet } from '@/types/SearchFacet';

import { WeakRouteLink } from '@/components/RouteLinks/RouteLinks';
import useWindowSize from '@/hooks/useWindowSize';
import { useAppDispatch } from '@/redux/hooks';
import { submitDeleteSavedSearch } from '@/redux/modules/savedSearch';
import { mergedCaterwaulAndHammerUIThemes } from '@/theme/mergedCaterwaulAndHammerUIThemes';
import cleanSearchTerms from '@/utils/cleanSearchTerms';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { toFacetLabelPairs, toFacetString, toSavedSearchUrl } from '@/utils/savedSearchFacets';

import { TableRow } from './TableRow';

type SavedSearch = {
    facets: Facet[];
    lastProcessed: string;
    lastSent: string;
    lastStatus: string;
    savedSearchId: number;
    searchURL: string;
    sendAlert: boolean;
    userId: number;
};

type Props = {
    searches: SavedSearch[];
    toggleSavedSearchEmail: Function;
};

export const SavedSearchTable = ({ searches = [], toggleSavedSearchEmail }: Props) => {
    // This is used for some nested functions with translations
    // This is NOT our standard!
    // DO NOT USE THIS IN THE COMPONENT ITSELF
    const intl = useIntl();

    const formatMessage = useAppFormatMessage();
    const { width } = useWindowSize();
    const dispatch = useAppDispatch();
    const {
        breakpoints: { tabletNarrowWidthNum },
    } = mergedCaterwaulAndHammerUIThemes;

    const renderFilters = (facets: Facet[], savedSearchUrl: string) => {
        const searchLabels = toFacetLabelPairs(facets, intl);
        return searchLabels.map((title) => (
            <Chip
                key={title}
                selected
            >
                <WeakRouteLink
                    className="line-clamp-1 truncate whitespace-normal no-underline"
                    title={title}
                    to={savedSearchUrl}
                >
                    {title}
                </WeakRouteLink>
            </Chip>
        ));
    };

    const mapSavedSearch = (search: SavedSearch) => {
        const date = search.lastSent.length ? parseISO(search.lastSent) : null;
        const savedSearchUrl = search?.searchURL || toSavedSearchUrl(search.facets);
        const title = cleanSearchTerms(toFacetString(search.facets, intl));

        return (
            <TableRow
                className="smMax:[&>:nth-child(1)]:grid-cols-[1/4]"
                email={
                    <Checkbox.Option
                        aria-label={String(
                            formatMessage(['notificationsPage', 'toggleEmailNotifications'], {
                                values: {
                                    name: title,
                                },
                            })
                        )}
                        checked={search.sendAlert}
                        className="mb-0"
                        name="emailCheckbox"
                        onChange={() => toggleSavedSearchEmail(search.savedSearchId)}
                    />
                }
                frequency={
                    width > tabletNarrowWidthNum &&
                    (search.lastSent === '0001-01-01T00:00:00' || search.lastSent === ''
                        ? formatMessage(['never'])
                        : shortFormatDate(date))
                }
                key={search.savedSearchId}
                type={
                    <div className="align-center flex items-center">
                        <IconButton
                            className="mr-sm"
                            icon={Trash}
                            onClick={() => dispatch(submitDeleteSavedSearch(search))}
                            size="sm"
                        />
                        <div className="flex max-w-full flex-wrap gap-sm overflow-hidden">
                            {renderFilters(search.facets, savedSearchUrl)}
                        </div>
                    </div>
                }
            />
        );
    };

    return (
        <>
            <TableRow
                email={
                    <Caption
                        className="uppercase"
                        color="secondary"
                    >
                        {formatMessage(['notificationsPage', 'table', 'header', 'emailAlert'])}
                    </Caption>
                }
                frequency={
                    <Caption
                        className="uppercase"
                        color="secondary"
                    >
                        {formatMessage(['notificationsPage', 'table', 'header', 'lastNewResult'])}
                    </Caption>
                }
                type={
                    <Caption
                        className="uppercase"
                        color="secondary"
                    >
                        {formatMessage(['notificationsPage', 'table', 'header', 'searchTerm'])}
                    </Caption>
                }
            />
            {searches.map(mapSavedSearch)}
        </>
    );
};
